import { useState, useRef, useEffect } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import {
  MessageCircle,
  X,
  Minimize2,
  Send,
  Bot,
  UserCircle,
  Loader2,
} from 'lucide-react';
import { Transition } from '@headlessui/react';
import { supportApi } from '../../api/support';
import { useToast } from '../Common/Toast';
import type { ChatMessage } from '../../types';

const quickQuestions = [
  'How do I fulfill an order?',
  'When is my next payout?',
  'How do I add variants?',
];

export function ChatWidget() {
  const { toast } = useToast();
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [input, setInput] = useState('');
  const [unread, setUnread] = useState(0);
  // Was a permanently-undefined constant, so the floating widget started a
  // brand-new conversation on every single message. Adopting the id the
  // backend returns is what makes the thread continuous.
  const [conversationId, setConversationId] = useState<string | undefined>();
  const messagesEndRef = useRef<HTMLDivElement>(null);

  // Seed from the vendor's ACTIVE thread — the same query the Support page
  // uses, so both surfaces are one conversation.
  //
  // Without this the widget minted a fresh thread on every page load, and
  // because `/conversations` serves only the most-recently-updated thread as
  // `current_conversation_id` (and there is no thread picker anywhere), that
  // two-message widget thread became what the Support page rendered — the
  // vendor's real transcript was no longer reachable in the UI, and "Talk to
  // Human" escalated the wrong row. The shared queryKey lets React Query
  // dedupe this with the Support page's own request.
  const { data: seed } = useQuery({
    queryKey: ['support', 'conversations'],
    queryFn: supportApi.getConversations,
  });

  useEffect(() => {
    if (!seed) return;
    if (seed.conversationId && !conversationId) {
      setConversationId(seed.conversationId);
    }
    if (seed.messages?.length && messages.length === 0) {
      setMessages(seed.messages);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [seed]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const sendMutation = useMutation({
    mutationFn: (content: string) => supportApi.sendMessage(content, conversationId),
    onSuccess: (response) => {
      setMessages((prev) => [...prev, response]);
      if (response.conversation_id && response.conversation_id !== conversationId) {
        setConversationId(response.conversation_id);
      }
      if (!isOpen) setUnread((prev) => prev + 1);
    },
    onError: () => toast('error', 'Failed to send message'),
  });

  const handleSend = () => {
    const content = input.trim();
    if (!content || sendMutation.isPending) return;

    const userMessage: ChatMessage = {
      id: crypto.randomUUID(),
      role: 'user',
      content,
      timestamp: new Date().toISOString(),
    };

    setMessages((prev) => [...prev, userMessage]);
    setInput('');
    sendMutation.mutate(content);
  };

  const handleOpen = () => {
    setIsOpen(true);
    setUnread(0);
  };

  return (
    <>
      {/* Floating Button — sm:bottom-6 desktop, bottom-20 mobile so it
          clears the bottom nav / safe area on phones. aria-label
          for screen readers. */}
      {!isOpen && (
        <button
          onClick={handleOpen}
          aria-label="Open AI support chat"
          className="fixed bottom-20 right-4 sm:bottom-6 sm:right-6 z-50 flex h-14 w-14 items-center justify-center rounded-full bg-primary text-white shadow-lg transition-transform hover:scale-105 active:scale-95"
        >
          <MessageCircle className="h-6 w-6" />
          {unread > 0 && (
            <span className="absolute -right-1 -top-1 flex h-5 w-5 items-center justify-center rounded-full bg-red-500 text-xs font-bold text-white">
              {unread}
            </span>
          )}
        </button>
      )}

      {/* Chat Panel — full-screen on phones (≤sm), floating window
          on tablet+. The viewport breakpoint is Tailwind's `sm`
          (640px). On phones we drop the rounded corners, sit flush
          against the edges, and shrink the height to leave room for
          the OS keyboard. */}
      <Transition show={isOpen}>
        <div className="fixed inset-x-0 bottom-0 sm:inset-auto sm:bottom-6 sm:right-6 z-50 flex h-[80vh] sm:h-[500px] w-full sm:w-[380px] flex-col overflow-hidden rounded-t-2xl sm:rounded-2xl border border-gray-200 bg-white shadow-2xl transition-all dark:border-gray-700 dark:bg-gray-800">
          {/* Header */}
          <div className="flex items-center justify-between bg-primary px-4 py-3">
            <div className="flex items-center gap-2">
              <Bot className="h-5 w-5 text-white" />
              <span className="text-sm font-semibold text-white">AI Support</span>
            </div>
            <div className="flex items-center gap-1">
              <button
                onClick={() => setIsOpen(false)}
                className="rounded p-1 text-white/70 hover:bg-white/10 hover:text-white"
              >
                <Minimize2 className="h-4 w-4" />
              </button>
              <button
                onClick={() => {
                  setIsOpen(false);
                  setMessages([]);
                }}
                className="rounded p-1 text-white/70 hover:bg-white/10 hover:text-white"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          </div>

          {/* Messages */}
          <div className="flex-1 overflow-y-auto p-3 space-y-3">
            {messages.length === 0 && (
              <div className="flex flex-col items-center py-8 text-center">
                <Bot className="mb-3 h-10 w-10 text-primary" />
                <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
                  How can I help?
                </p>
                <div className="mt-4 flex flex-col gap-2">
                  {quickQuestions.map((q) => (
                    <button
                      key={q}
                      onClick={() => {
                        const userMessage: ChatMessage = {
                          id: crypto.randomUUID(),
                          role: 'user',
                          content: q,
                          timestamp: new Date().toISOString(),
                        };
                        setMessages((prev) => [...prev, userMessage]);
                        setInput('');
                        sendMutation.mutate(q);
                      }}
                      className="rounded-lg border border-gray-200 px-3 py-2 text-xs text-gray-600 hover:bg-gray-100 dark:border-gray-700 dark:text-gray-400 dark:hover:bg-gray-700"
                    >
                      {q}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {messages.map((msg) => (
              <div
                key={msg.id}
                className={`flex items-start gap-2 ${msg.role === 'user' ? 'flex-row-reverse' : ''}`}
              >
                <div className="shrink-0">
                  {msg.role === 'user' ? (
                    <div className="flex h-7 w-7 items-center justify-center rounded-full bg-primary">
                      <UserCircle className="h-4 w-4 text-white" />
                    </div>
                  ) : (
                    <div className="flex h-7 w-7 items-center justify-center rounded-full bg-purple-100 dark:bg-purple-900/50">
                      <Bot className="h-4 w-4 text-purple-600 dark:text-purple-400" />
                    </div>
                  )}
                </div>
                <div
                  className={`max-w-[75%] rounded-xl px-3 py-2 text-xs leading-relaxed ${
                    msg.role === 'user'
                      ? 'bg-primary text-white'
                      : 'bg-gray-100 text-gray-900 dark:bg-gray-700 dark:text-gray-100'
                  }`}
                >
                  {msg.content}
                </div>
              </div>
            ))}

            {sendMutation.isPending && (
              <div className="flex items-start gap-2">
                <div className="flex h-7 w-7 items-center justify-center rounded-full bg-purple-100 dark:bg-purple-900/50">
                  <Bot className="h-4 w-4 text-purple-600 dark:text-purple-400" />
                </div>
                <div className="flex items-center gap-1.5 rounded-xl bg-gray-100 px-3 py-2 text-xs text-gray-500 dark:bg-gray-700">
                  <Loader2 className="h-3 w-3 animate-spin" />
                  Thinking...
                </div>
              </div>
            )}

            <div ref={messagesEndRef} />
          </div>

          {/* Input */}
          <div className="border-t border-gray-200 p-3 dark:border-gray-700">
            <div className="flex items-center gap-2">
              <input
                type="text"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleSend()}
                placeholder="Type a message..."
                className="flex-1 rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-xs focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100"
              />
              <button
                onClick={handleSend}
                disabled={!input.trim() || sendMutation.isPending}
                className="rounded-lg bg-primary p-2 text-white disabled:opacity-50"
              >
                <Send className="h-3.5 w-3.5" />
              </button>
            </div>
          </div>
        </div>
      </Transition>
    </>
  );
}
